// Detects the non-physiological "lead-in" transient at the very start of a
// recording.
//
// AliveCor/Kardia devices apply an AC-coupling / high-pass filter; when the
// recording starts, that filter produces a large decaying excursion (e.g. the
// signal swings ~1 mV away from baseline and converges over a few hundred ms)
// before it settles. The AliveCor viewer ignores this segment. Its length is
// NOT fixed — it depends on the initial DC offset, so it must be detected per
// recording (one recording may need ~0.5 s trimmed, another none at all).
//
// Strategy: for each lead, find the first moment the signal stays "quiet"
// (close to its baseline) for a short, continuous window — that marks the end
// of the settling transient. We return the largest such index across all leads
// so every lead is trimmed by the same amount and stays time-aligned.

const QUIET_THRESHOLD_MV = 0.15; // within 0.15 mV of baseline counts as quiet
const QUIET_WINDOW_S = 0.1; // must stay quiet this long to be "settled"
const SEARCH_WINDOW_S = 2.0; // only look for the transient in the first 2 s

function median(values: number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Returns the number of leading samples to trim so the displayed signal starts
 * at the true beginning of the test (0 if there's no settling transient).
 */
export function detectEcgLeadIn(
  leadsSamplesMv: number[][],
  frequencyHz: number
): number {
  if (leadsSamplesMv.length === 0 || frequencyHz <= 0) return 0;

  const need = Math.max(1, Math.round(QUIET_WINDOW_S * frequencyHz));
  const searchLimit = Math.round(SEARCH_WINDOW_S * frequencyHz);

  let cut = 0;

  leadsSamplesMv.forEach((samples) => {
    if (samples.length === 0) return;

    // Baseline estimated from a stable region after the search window; fall
    // back to the whole lead for very short recordings.
    const stableRegion = samples.slice(
      searchLimit,
      searchLimit + frequencyHz * 4
    );
    const baseline = median(stableRegion.length > 0 ? stableRegion : samples);

    const limit = Math.min(searchLimit, samples.length);
    let count = 0;
    let settle = 0;

    for (let i = 0; i < limit; i += 1) {
      if (Math.abs(samples[i] - baseline) < QUIET_THRESHOLD_MV) {
        count += 1;
        if (count >= need) {
          settle = i - need + 1;
          break;
        }
      } else {
        count = 0;
      }
    }

    if (settle > cut) cut = settle;
  });

  return cut;
}
